import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin, ArrowRight } from "lucide-react";
import { useLang } from "@/App";
import { Reveal, ChapterHead } from "@/components/Section";

const IMG_BG = "https://prieure-saint-leonard.fr/api/files/prieure-saint-leonard/uploads/444a16702ec5486e9642bcc3a5803123.jpg";

const Inventions = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section id="inventions" ref={ref} data-testid="inventions-section" className="relative px-5 md:px-10 py-28 md:py-40 bg-inkdeep overflow-hidden scroll-mt-20">
      <div className="absolute inset-0 opacity-[0.10]">
        <motion.img src={IMG_BG} alt="" style={{ y }} className="w-full h-[120%] object-cover" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-inkdeep via-transparent to-inkdeep" />

      <div className="relative max-w-[1600px] mx-auto">
        <ChapterHead kicker={t.inventions.kicker} title={t.inventions.title} dark testId="inventions" />

        <Reveal delay={0.1}>
          <div className="space-y-6 max-w-3xl -mt-6 md:-mt-10">
            {t.inventions.paragraphs.map((p, i) => (
              <p key={i} data-testid={`inventions-paragraph-${i}`} className="font-display text-xl md:text-2xl leading-relaxed text-paper/75 first:text-paper/90">
                {p}
              </p>
            ))}
          </div>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-5 mt-16" data-testid="inventions-cards">
          {t.inventions.cards.map((c, i) => (
            <Reveal key={i} delay={0.1 + i * 0.08}>
              <div
                data-testid={`invention-card-${i}`}
                className={`group relative h-full p-8 md:p-10 border transition-colors ${
                  i === 0 ? "border-gold/70 bg-gold/[0.06]" : "border-linedark bg-inkdeep/60 hover:border-bronze"
                }`}
              >
                {i === 0 && (
                  <span className="absolute top-5 right-5 font-display italic text-4xl text-gold/80">2030</span>
                )}
                <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-terraBright mb-4">{c.tag}</p>
                <h3 className="font-display font-light text-3xl md:text-4xl text-paper leading-tight">{c.name}</h3>
                <p className="text-sm text-paper/60 leading-relaxed mt-4">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-16">
          <blockquote data-testid="inventions-hommage" className="border-l-2 border-gold/60 pl-6 md:pl-8 max-w-3xl">
            <p className="font-display italic text-xl md:text-2xl text-gold/85 leading-snug">
              {t.inventions.hommage}
            </p>
          </blockquote>
        </Reveal>

        <Reveal delay={0.25} className="mt-12">
          <div className="flex flex-wrap gap-4">
            <Link
              data-testid="inventions-detail-link"
              to="/ingenia"
              className="group inline-flex items-center gap-3 bg-gold text-inkdeep hover:bg-paper transition-colors px-8 py-4 font-mono2 text-[11px] uppercase tracking-[0.2em]"
            >
              {t.inventions.detailCta}
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              data-testid="inventions-ingenia-link"
              href="https://www.ingenia-services.com"
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-3 bg-terra text-paper hover:bg-terraBright transition-colors px-8 py-4 font-mono2 text-[11px] uppercase tracking-[0.2em]"
            >
              {t.inventions.industrialCta}
              <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
            <a
              data-testid="inventions-linkedin-link"
              href="https://www.linkedin.com/in/gdechassey"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-3 border border-paper/40 text-paper hover:border-gold hover:text-gold transition-colors px-8 py-4 font-mono2 text-[11px] uppercase tracking-[0.2em]"
            >
              <Linkedin size={14} /> {t.inventions.linkedinCta}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Inventions;
